import { useEffect, useRef, useState } from 'react';
import { ReactComponent as ChevronLeftSvg } from '../../../assets/svg/chevron-left.svg';
import ViewPhoto from './viewPhoto';
import request from '../../../lib/request';
import Title from '../../../components/dialog/title';

const sumCalories = (arr) => {
  return arr.reduce((acc, cur) => {
    return acc + (cur.calorie ? cur.calorie : 0);
  }, 0);
};

const sumDetail = (arr, key) => {
  return arr.reduce((acc, cur) => {
    return (
      acc +
      (cur.ingredients.length > 0
        ? cur.ingredients.reduce((iAcc, iCur) => {
            return iAcc + (iCur[key] ? iCur[key] : 0);
          }, 0)
        : 0)
    );
  }, 0);
};

const FeedPhoto = ({
  isDialogOpen,
  feedDialogRef,
  closeDialog,
  isIngredientDialogOpen,
  setIsIngredientDialogOpen,
}) => {
  const viewDialogRef = useRef(null);
  const observer = useRef();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [logList, setLogList] = useState({});
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (isDialogOpen) {
      getFeedList({ count: 18, page });
    } else {
      setPage(1);
      setLogList({});
      setHasMore(true);
    }
  }, [isDialogOpen, page]);

  const getFeedList = async ({ count = 18, page = 1 }) => {
    try {
      setLoading(true);
      const { result } = await request.get(`/meal?count=${count}&page=${page}`);
      if (result) {
        if (result.length === 0) {
          setHasMore(false); // Set hasMore to false if no more posts are returned
        } else {
          result.forEach((res) => {
            setLogList((prev) => {
              if (prev[res.logDate]) {
                if (!prev[res.logDate][res.id]) {
                  prev[res.logDate][res.id] = res;
                }
              } else {
                prev[res.logDate] = { [res.id]: res };
              }

              return prev;
            });
          });
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const lastPostElementRef = (node) => {
    if (loading || !hasMore) {
      return; // Stop observing if loading or no more posts
    }
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prev) => prev + 1); // Trigger loading of new posts by changing page number
      }
    });

    if (node) {
      observer.current.observe(node);
    }
  };

  const handleClose = () => {
    closeDialog();
  };

  const handleSelectView =
    ({ logDate, id }) =>
    () => {
      if (logList[logDate]) {
        const selectedLog = logList[logDate][id];
        if (selectedLog) {
          setSelected(selectedLog);
          openViewDialog();
        }
      }
    };

  const openViewDialog = () => {
    if (viewDialogRef.current) {
      viewDialogRef.current.showModal();
      setDialogOpen(true);
    }
  };

  const closeViewDialog = () => {
    if (viewDialogRef.current) {
      viewDialogRef.current.close();
      setDialogOpen(false);
      setSelected({});
    }
  };

  return (
    <dialog
      ref={feedDialogRef}
      className={`w-full h-full max-w-lg max-h-full p-4 bg-primary ${
        dialogOpen && 'h-10 overflow-hidden'
      }`}
    >
      <Title
        title='Daily Calorie Tracker'
        onClick={handleClose}
        color='white'
      />
      {Object.entries(logList).map(([key, obj], i) => (
        <div
          key={i}
          className='mb-4'
        >
          <div className='font-bold text-white mb-1'>{key}</div>
          <div className='flex items-center justify-between mb-2 text-[11px] text-white'>
            {sumCalories(Object.values(obj)) > 0 && (
              <div className='px-2 rounded-full border bg-tertiary/50 text-center font-bold'>
                {sumCalories(Object.values(obj))} Cal
              </div>
            )}
            {sumDetail(Object.values(obj), 'proteins') > 0 && (
              <div className='px-2 rounded-full border border-protein bg-protein/30'>
                Protein{' '}
                <span className='font-bold'>
                  {sumDetail(Object.values(obj), 'proteins').toFixed(1)}g
                </span>
              </div>
            )}
            {sumDetail(Object.values(obj), 'carbohydrates') > 0 && (
              <div className='px-2 rounded-full border border-carbs bg-carbs/30'>
                Carbs{' '}
                <span className='font-bold'>
                  {sumDetail(Object.values(obj), 'carbohydrates').toFixed(1)}g
                </span>
              </div>
            )}
            {sumDetail(Object.values(obj), 'fats') > 0 && (
              <div className='px-2 rounded-full border border-fats bg-fats/30'>
                Fat{' '}
                <span className='font-bold'>
                  {sumDetail(Object.values(obj), 'fats').toFixed(1)}g
                </span>
              </div>
            )}
          </div>
          <div className='grid grid-cols-3 gap-1'>
            {Object.values(obj).map((item, index) => (
              <div
                key={index}
                className='relative'
              >
                <img
                  src={item.photoUrl}
                  alt=''
                  className='object-cover border border-tertiary rounded-xl aspect-square'
                  onClick={handleSelectView({
                    logDate: item.logDate,
                    id: item.id,
                  })}
                  ref={
                    Object.values(obj).length === index + 1
                      ? lastPostElementRef
                      : null
                  }
                />
                {item.calorie && (
                  <div className='absolute bottom-0 right-0 bg-tertiary text-white rounded-tl-xl rounded-br-xl px-1 text-xs font-bold'>
                    {item.calorie} Cal
                  </div>
                )}
                {item.tags.length > 0 && (
                  <div className='absolute top-1 left-1 bg-black bg-opacity-60 text-white rounded-full px-1 text-[10px] line-clamp-1 max-w-[calc(100%-8px)]'>
                    {item.tags.map((tag) => `#${tag.tag.name}`).join(' ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <ViewPhoto
        viewDialogRef={viewDialogRef}
        closeDialog={closeViewDialog}
        mealInfo={selected}
        handleRefresh={async () => {
          setPage(1);
          setHasMore(true);
          closeViewDialog();
        }}
        isIngredientDialogOpen={isIngredientDialogOpen}
        setIsIngredientDialogOpen={setIsIngredientDialogOpen}
      />
    </dialog>
  );
};

export default FeedPhoto;
